import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchJobDetails, updateJob } from '../redux/JobsSlice';
import { fetchJobCategories } from '../redux/JobCategoriesSlice';
import { ArrowLeft, Edit2, Save } from 'lucide-react';
import { toast } from 'react-toastify';
import { formatDate } from '../utils/Config';

const JobDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  
  const job = useSelector((state) => state.jobs.selectedJob);
  const categories = useSelector((state) => state.jobCategories.items);
  console.log(categories)
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    job_category_id: '',
    job_type: '',
    location: '',
    location_type: 'on-site',
    company_name: '',
    company_website: '',
    company_logo: null,
    salary_min: '',
    salary_max: '',
    salary_currency: 'GHS',
    benefits: '',
    qualifications: '',
    experience_years: '',
    responsibilities: '',
    skills: '',
    application_deadline: '',
    posted_at:'',
    application_link: '',
    contact_email: '',
    status: 'active',
    appliable: 'true'
  });

  const currencies = [
    { value: 'USD', label: 'US Dollar (USD)' },
    { value: 'EUR', label: 'Euro (EUR)' },
    { value: 'GHS', label: 'Ghana Cedi (GHS)' },
    { value: 'GBP', label: 'British Pound (GBP)' }
  ];

  const jobTypes = [
    { value: 'full-time', label: 'Full Time' },
    { value: 'part-time', label: 'Part Time' },
    { value: 'contract', label: 'Contract' },
    { value: 'freelance', label: 'Freelance' }
  ];

  const locationTypes = [
    { value: 'remote', label: 'Remote' },
    { value: 'on-site', label: 'On-site' },
    { value: 'hybrid', label: 'Hybrid' },
  ];

  useEffect(() => {
    dispatch(fetchJobDetails(id));
    dispatch(fetchJobCategories());
  }, [dispatch, id]);

  useEffect(() => {
    if (job) {
      setFormData(job);
      if (job.company_logo) {
        setLogoPreview(job.company_logo);
      }
    }
  }, [job]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Logo file size should not exceed 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error('Please upload an image file');
        return;
      }

      setFormData(prev => ({
        ...prev,
        company_logo: file
      }));

      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formPayload = new FormData();
      
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null) {
          formPayload.append(key, formData[key]);
        }
      });

      const result = await dispatch(updateJob({ id, data: formPayload })).unwrap();
      if (result) {
        toast.success('Job updated successfully');
        setIsEditing(false);
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update job');
    } finally {
      setLoading(false);
    }
  };

  const renderField = (label, value) => (
    <div className="mb-4">
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1">{value}</p>
    </div>
  );

  const renderForm = () => (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Job title
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            name="job_category_id"
            value={formData.job_category_id}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          >
            {categories?.map(category => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        {/* Add all other form fields similar to AddJobPage component */}
        {/* Include all the fields from your form state */}
        
        {/* Example of a few more fields */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Job Type
          </label>
          <select
            name="job_type"
            value={formData.job_type}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          >
            {jobTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Location Type
          </label>
          <select
            name="location_type"
            value={formData.location_type}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          >
            {locationTypes.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Name
          </label>
          <input
            type="text"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Website
          </label>
          <input
            type="url"
            name="company_website"
            value={formData.company_website}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Logo
          </label>
          <input
            type="file"
            name="company_logo"
            onChange={handleLogoChange}
            accept="image/*"
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          />
          {logoPreview && (
            <img
              src={logoPreview}
              alt="Company Logo Preview"
              className="mt-2 h-20 object-contain"
            />
          )}
        </div>

        {/* Salary Information Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Salary Currency
          </label>
          <select
            name="salary_currency"
            value={formData.salary_currency}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          >
            {currencies.map(currency => (
              <option key={currency.value} value={currency.value}>
                {currency.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Minimum Salary
          </label>
          <input
            type="number"
            name="salary_min"
            value={formData.salary_min}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Maximum Salary
          </label>
          <input
            type="number"
            name="salary_max"
            value={formData.salary_max}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Years of Experience Required
          </label>
          <input
            type="number"
            name="experience_years"
            value={formData.experience_years}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
      </div>

      {/* Full Width Text Areas */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Job Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={4}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Responsibilities
          </label>
          <textarea
            name="responsibilities"
            value={formData.responsibilities}
            onChange={handleChange}
            rows={4}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Qualifications
          </label>
          <textarea
            name="qualifications"
            value={formData.qualifications}
            onChange={handleChange}
            rows={4}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Required Skills
          </label>
          <textarea
            name="skills"
            value={formData.skills}
            onChange={handleChange}
            rows={4}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Benefits
          </label>
          <textarea
            name="benefits"
            value={formData.benefits}
            onChange={handleChange}
            rows={4}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Application Details Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Posted on
          </label>
          <input
            type="date"
            name="posted_at"
            value={formData.posted_at}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Application Deadline
          </label>
          <input
            type="date"
            name="application_deadline"
            value={formData.application_deadline}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Application Link
          </label>
          <input
            type="url"
            name="application_link"
            value={formData.application_link}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Company Website
          </label>
          <input
            type="url"
            name="company_website"
            value={formData.company_website}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Contact Email
          </label>
          <input
            type="email"
            name="contact_email"
            value={formData.contact_email}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            name="status"
            value={formData.status}
            onChange={handleChange}
            className="w-full p-2 border rounded-md focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="open">Open</option>
            <option value="draft">Draft</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            name="appliable"
            checked={formData.appliable ===  1}
            onChange={(e) => handleChange({
              target: {
                name: 'appliable',
                value: e.target.checked ? 'true' : 'false',
                type: 'text'
              }
            })}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-700">
            Allow applications through platform
          </label>
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={() => setIsEditing(false)}
          className="px-4 py-2 text-gray-600 border rounded-md hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );

  const renderDetails = () => (
    <div className="space-y-6">
        <img
        src={job?.company_logo}
        className='max-h-40 max-w-40 rounded shadow object-contain'
        />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderField('Title', job?.title)}
        {renderField('Category', categories?.find(c => c.id == job?.job_category.id)?.name)}
        {renderField('Job Type', job?.job_type)}
        {renderField('Location', job?.location)}
        {renderField('Location Type', job?.location_type)}
        {renderField('Company', job?.company_name)}
        {renderField('Salary Range', `${job?.salary_currency} ${job?.salary_min} - ${job?.salary_max}`)}
        {renderField('Experience Required', `${job?.experience_years} years`)}
      </div>

      {renderField('Description', job?.description)}
      {renderField('Responsibilities', job?.responsibilities)}
      {renderField('Qualifications', job?.qualifications)}
      {renderField('Benefits', job?.benefits)}
      {renderField('Skills', job?.skills)}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {renderField('Application Deadline', formatDate(job?.application_deadline))}
        {renderField('Posted On', formatDate(job?.posted_at))}
        {renderField('Application Link', job?.application_link)}
        {renderField('Company Website', job?.company_website)}
        {renderField('Contact email', job?.contact_email)}
        {renderField('Status', job?.status)}
      </div>
      <div className="flex items-center">
          <input
            type="checkbox"
            disabled
            checked={formData.appliable ===  1}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label className="ml-2 block text-sm text-gray-700">
            Allow applications through platform
          </label>
        </div>

      <div className="flex justify-end">
        <button
          onClick={() => setIsEditing(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Edit2 className="w-4 h-4 mr-2" />
          Edit Job
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-5xl mx-auto p-6">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back
      </button>
      
      <h2 className="text-2xl font-bold mb-6">
        {isEditing ? 'Edit Job' : 'Job Details'}
      </h2>

      {isEditing ? renderForm() : renderDetails()}
    </div>
  );
};

export default JobDetail;